// Helpers for separating a token's actual word from surrounding punctuation.
// e.g. "da?" -> { lead:"", core:"da", trail:"?" }; "da" is the word, "?" is
// punctuation that should display but never be treated as a timed/lookupable word.
//
// NOT punctuation here: apostrophes (’ ' ʼ ‘) and ^ are LETTERS in these
// orthographies (glottal/epiglottal stops — Bunun ka’a’, Atayal pitu’, ’laqi,
// Amis pala^, fo’is), so a word may begin or end with them. Unicode \p{P}
// wrongly classes ’ as punctuation, which split ka’a’ into ka’a + trailing ’.
// Edges are trimmed of anything that is NOT a letter/digit/apostrophe/^.
//
// Saisiyat marks phonemic vowel LENGTH with a colon (Sahala:, SayboSi:, hahila:),
// so ':' is a letter there and must stay in the word — but it is quotative
// punctuation ("said:") in every other language. Callers pass `dialect` so the
// colon is kept in-word for Saisiyat only.

const KEEP = "\\p{L}\\p{N}'’ʼ‘^";
const EDGE = new RegExp(`^[^${KEEP}]+|[^${KEEP}]+$`, 'gu');
const LEAD = new RegExp(`^[^${KEEP}]+`, 'u');
const TRAIL = new RegExp(`[^${KEEP}]+$`, 'u');
const EDGE_C = new RegExp(`^[^${KEEP}:]+|[^${KEEP}:]+$`, 'gu');
const LEAD_C = new RegExp(`^[^${KEEP}:]+`, 'u');
const TRAIL_C = new RegExp(`[^${KEEP}:]+$`, 'u');
const keepColon = (dialect) => dialect === 'saisiyat';

export function splitToken(text, dialect) {
  const t = text || '';
  const c = keepColon(dialect);
  const lead = (t.match(c ? LEAD_C : LEAD) || [''])[0];
  const rest = t.slice(lead.length);
  const trail = (rest.match(c ? TRAIL_C : TRAIL) || [''])[0];
  const core = rest.slice(0, rest.length - trail.length);
  return { lead, core, trail };
}

// The bare word, stripped of leading/trailing punctuation. Used for vocab
// status, gloss, and dictionary lookups so "da" and "da?" are the same word.
export function cleanWord(text, dialect) {
  return (text || '').replace(keepColon(dialect) ? EDGE_C : EDGE, '');
}

// Key for the review screen's word -> dialects table, and the one place that
// table's two sides agree.
//
// The table exists to answer "this card has no dialect saved on it — which
// languages is the word in?", so its key must be derivable WITHOUT knowing the
// dialect. cleanWord alone cannot be that key: it keeps ’ and (for Saisiyat)
// the phonemic colon as the letters they are, and the bake knows the dialect
// while the lookup does not — so ka’a’ and hahila: were keyed with those
// letters and looked up without them, and every card on such a word missed.
//
// Hence a second, dialect-blind pass over the edges. The cost is that words
// differing only by an edge apostrophe share a key and their dialect lists
// merge; that is the right trade for a table whose only job is deciding which
// review queue a legacy card belongs in.
const EDGE_P = /^[\p{P}\s]+|[\p{P}\s]+$/gu;

export function reviewKey(text, dialect) {
  return cleanWord(text, dialect).replace(EDGE_P, '').toLowerCase();
}

// How a word becomes an identity in the stores: "<dialect>:<word>".
//
// This is sixteen languages sharing one Latin orthography, so the same spelling
// is a different word in each — and the stores must agree on that. Vocab always
// did. Glosses did NOT: they were keyed on the bare word, so a correction to
// Amis `ina` overwrote the Bunun one on the device, and a correction recovered
// from the cloud arrived with no way to tell which of sixteen dictionaries it
// belonged to. One builder now, so the two cannot drift apart again.
export const entryKey = (dialect, word) => `${dialect}:${(word || '').toLowerCase()}`;
